import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { DEFAULT_PAGE_SIZE } from '@/shared/types/pagination';
import { projectMembersService } from '../api/projectMembers.service';
import { PROJECT_KEYS } from './queryKeys';
import type { ProjectMember } from '../types';

export interface UseProjectMembersSearchResult {
  data: ProjectMember[];
  totalCount: number;
  isLoading: boolean;
  isFetching: boolean;
}

/**
 * Searchable, paginated project members — for pickers where the project's
 * membership can't be assumed to be small (see `useProjectMembers` for the
 * unpaginated variant used by the member-management UI).
 *
 * @param search - Optional search term sent as `SearchTerm` to the API
 * @param pageIndex - Zero-based page index (converted to 1-based internally)
 * @param pageSize - Number of items per page
 */
export function useProjectMembersSearch(
  projectId: string,
  search: string,
  pageIndex = 0,
  pageSize = DEFAULT_PAGE_SIZE,
): UseProjectMembersSearchResult {
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  const params = {
    SearchTerm: search || undefined,
    Page: pageIndex + 1,
    PageSize: pageSize,
  };

  const { data, isLoading, isFetching } = useQuery({
    queryKey: PROJECT_KEYS.membersSearch(orgId ?? '', projectId, params),
    queryFn: ({ signal }) => projectMembersService.search(projectId, params, signal),
    staleTime: 60_000,
    enabled: !!orgId && !!projectId,
    placeholderData: keepPreviousData,
  });

  return {
    data: data?.items ?? [],
    totalCount: data?.totalCount ?? 0,
    isLoading,
    isFetching,
  };
}
